/**
 * Handles the behaviour of the address fields,
 * allowing switching between postcode lookup and
 * manual entry
 * @module embed/address
 */

import { delegate } from '@/lib/events';
import { busyWhile } from '@/lib/busy';
import { renderOptions } from '@/lib/forms';
import { show, hide } from '@/lib/show_hide';
import { withoutValidation } from './form_validation';
import { escapeHtml } from '@/lib/string';
import withErrorRender from '@/lib/withErrorRender';
import { ERROR_MESSAGE } from '@/../packs/embed';

const SELECTOR = '.js-address';
const SELECTOR_LOOKUP = '.js-address__lookup';
const SELECTOR_INPUT = '.js-address__input';
const SELECTOR_LIST = '.js-address__list';
const SELECTOR_POSTCODE_FIELDS = '.js-address__postcode-fields';
const SELECTOR_POSTCODE_FIELD = '.js-address__postcode';
const SELECTOR_ADDRESS_FIELD = '.js-address__addressList';
const SELECTOR_SEARCH = '.js-address__search';
const SELECTOR_NO_RESULTS = '.js-address__no-results';
const SELECTOR_WITH_RESULTS = '.js-address__with-results';
export const SELECTOR_SHOW_LOOKUP = '.js-address__showLookup';
const SELECTOR_SHOW_INPUT = '.js-address__showInput';
export const SELECTOR_ADDRESS_SUPPLY = '.js-address--supply';

const ATTR_SUBMITTABLE_ID = 'data-submittable-id';
const ATTR_CLEAR_ON_SHOW = 'data-address-clear-on-show';

// Cache the current validator so it can be reused when creating new address fields
let currentValidator;

export function prepareTemplate(el) {
  const clone = el.cloneNode(true);
  // Cleanup a few CSS classes specific to the present address
  clone.querySelector(SELECTOR).classList.remove('js-address--supply');
  clone.querySelector(SELECTOR_SEARCH).classList.add('no-visible-disabled');
  clone.querySelector(SELECTOR_POSTCODE_FIELD).classList.remove('js-no-enable');
  // Reset the form
  Array.from(clone.querySelectorAll(SELECTOR_POSTCODE_FIELD)).forEach(el =>
    el.setAttribute('value', '')
  );
  // Reset the state of the clone
  show(clone.querySelector(SELECTOR_POSTCODE_FIELDS), { enable: true });
  hideResults(clone);
  showLookup(clone);
  return clone.outerHTML;
}
export function create(template, client, { type }) {
  const parser = new DOMParser();

  const el = parser.parseFromString(
    replaceNameAndIds(template, type),
    'text/html'
  );
  Array.from(el.querySelectorAll(SELECTOR_SEARCH), el =>
    el.classList.remove('d-none')
  );
  // Clear the values
  Array.from(el.querySelectorAll('input:not([type="hidden"])'), control =>
    control.setAttribute('value', '')
  );
  Array.from(el.querySelectorAll('select'), el => (el.value = ''));
  Array.from(el.querySelectorAll('[selected]'), el =>
    el.removeAttribute('selected')
  );
  setupAddresses(el, client, currentValidator);
  return el.body.firstChild;
}

const TEMPLATE_IDENTIFIER = 'present';
function replaceNameAndIds(template, type) {
  return template.replace(new RegExp(`${TEMPLATE_IDENTIFIER}`, 'g'), `${type}`);
}

export default function setupAddresses(el, client, validator) {
  currentValidator = validator;
  Array.from(el.querySelectorAll(SELECTOR)).forEach(el => {
    setupAddress(el, client, validator);
  });
}

function setupAddress(el, client, validator) {
  // Make non-focusable-by-default elements focusable
  // ahead of the focus movements
  Array.from(
    el.querySelectorAll([SELECTOR_NO_RESULTS, SELECTOR_INPUT, SELECTOR_LOOKUP])
  ).forEach(elem => elem.setAttribute('tabindex', '-1'));

  // Cache a couple of things from the DOM
  const searchButton = el.querySelector(SELECTOR_SEARCH);
  const submittableId = el.getAttribute(ATTR_SUBMITTABLE_ID);

  function loadAddresses(options) {
    if (!options.postcode) return;

    searchButton.classList.add('is-busy');
    searchButton.disabled = true;
    return busyWhile(
      el,
      withErrorRender(
        client
          .getAddressList({
            ...options,
            submittable_id: submittableId
          })
          .then(addresses => renderAddresses(el, addresses)),
        {
          scope: el,
          beforeSelector: SELECTOR_POSTCODE_FIELDS,
          message: ERROR_MESSAGE
        }
      )
    ).finally(() => {
      searchButton.classList.remove('is-busy');
      searchButton.disabled = false;
    });
  }

  function lookupPostcode(postcodeFieldEl) {
    return validator.validateField(postcodeFieldEl.name).then(function(status) {
      if (status == 'Valid') {
        return loadAddresses({
          postcode: postcodeFieldEl.value
        });
      }
      return status;
    });
  }

  if (el.hasAttribute(ATTR_CLEAR_ON_SHOW)) {
    el.addEventListener('willShow', () => {
      clearAddressFields(el, {
        ignore: control => control.getAttribute('type') == 'hidden'
      });
      hideResults(el);
      showLookup(el, { notify: false });
    });
  }

  el.addEventListener(
    'click',
    delegate(SELECTOR_SEARCH, e => {
      e.preventDefault();
      const postcodeEl = el.querySelector(SELECTOR_POSTCODE_FIELD);
      lookupPostcode(postcodeEl);
    })
  );

  // Handle enter presses in the postcode field
  el.addEventListener(
    'keydown',
    delegate(`${SELECTOR_LOOKUP} ${SELECTOR_POSTCODE_FIELD}`, e => {
      if (!searchButton.disabled) {
        if (e.keyCode == 13) {
          e.preventDefault();
          lookupPostcode(e.target);
        }
      }
    })
  );

  el.addEventListener(
    'click',
    delegate(SELECTOR_SHOW_INPUT, e => {
      e.preventDefault();
      showInput(el);
    })
  );

  el.addEventListener(
    'click',
    delegate(SELECTOR_SHOW_LOOKUP, e => {
      e.preventDefault();
      showLookup(el);
    })
  );

  // Synchronizes field values
  el.addEventListener(
    'change',
    delegate(SELECTOR_POSTCODE_FIELD, e => {
      Array.from(el.querySelectorAll(`[name="${e.target.name}"]`)).forEach(
        el => (el.value = e.target.value)
      );
    })
  );
}

function clearAddressFields(el, { ignore, notify }) {
  // Clear values and invalid state
  Array.from(el.querySelectorAll('input,select')).forEach(control => {
    if (ignore && !ignore(control)) {
      control.value =
        control.tagName == 'SELECT' ? control.options[0].value : '';
      if (notify && notify(control)) {
        const event = new CustomEvent('change', { bubbles: true });
        control.dispatchEvent(event);
      }
      control.classList.remove('is-invalid');
      control.removeAttribute('aria-invalid');
    }
  });
  // Remove error messages
  Array.from(el.querySelectorAll('.fv-plugins-message-container')).forEach(
    errorEl => (errorEl.innerHTML = '')
  );
}

// Handle the update of address list
function renderAddresses(el, addresses = []) {
  const noResults = el.querySelector(SELECTOR_NO_RESULTS);
  const withResults = el.querySelector(SELECTOR_WITH_RESULTS);
  const list = el.querySelector(SELECTOR_LIST);
  if (addresses.length) {
    const select = el.querySelector(SELECTOR_ADDRESS_FIELD);
    // Dismiss validations the time for the select field to be populated
    // otherwise the reset to a blank value triggers the required validation
    withoutValidation(select, () => {
      renderOptions(select, renderOptionEls(addresses), {
        preserveValue: false
      });
    });
    hide(noResults);
    show(withResults);
    // Focus the field to announce the results
    select.focus();
  } else {
    show(noResults, { focus: true });
    hide(withResults);
  }
  show(list);
}

function renderOptionEls(addresses) {
  // Prompt doubles as announcement for screen reader users
  const prompt = 'Please select...';
  return (
    `<option value="">${prompt}</option>` +
    addresses
      .map(
        address =>
          `<option value="${escapeHtml(
            JSON.stringify(address.parts)
          )}" ${mpanMprnAttributes(address)}>${formatAddressLabel(
            address
          )}</option>`
      )
      .join('')
  );
}

function mpanMprnAttributes(address) {
  return (
    ['mpan', 'mprn']
      .reduce((attrs, type) => {
        if (address.parts[type]) {
          attrs.push(`data-${type}="${address.parts[type]}"`);
        }
        return attrs;
      }, [])
      // Remove empty values
      .filter(i => i)
      .join(' ') || ''
  );
}

function formatAddressLabel(address) {
  return (
    address.parts.house_number +
    (address.parts.line1 ? `, ${address.parts.line1}` : '')
  );
}

function hideResults(el) {
  hide(el.querySelector(SELECTOR_NO_RESULTS));
  hide(el.querySelector(SELECTOR_WITH_RESULTS));
}

// Handles the change from lookup to input
function showInput(el) {
  hide(el.querySelector(SELECTOR_LOOKUP), { disable: true });
  // Don't hide the results for the supplier address
  if (!el.matches(SELECTOR_ADDRESS_SUPPLY)) {
    hide(el.querySelector(SELECTOR_NO_RESULTS), { disable: true });
    hide(el.querySelector(SELECTOR_WITH_RESULTS), { disable: true });
  }
  show(el.querySelector(SELECTOR_INPUT), { focus: true, enable: true });
  const event = new CustomEvent('didShowAddressInput', {
    bubbles: true
  });
  el.dispatchEvent(event);
}

function showLookup(
  el,
  // By default notify changes
  { notify = control => control.matches(SELECTOR_ADDRESS_FIELD) } = {}
) {
  hide(el.querySelector(SELECTOR_INPUT), { disable: true });
  const lookupFields = el.querySelector(SELECTOR_LOOKUP);
  show(lookupFields, { focus: true, enable: true });
  clearAddressFields(lookupFields, {
    // Postcode fields would logically be the same between both
    ignore: control =>
      control.type == 'hidden' || control.matches(SELECTOR_POSTCODE_FIELD),
    notify
  });
}



// WEBPACK FOOTER //
// ./app/webpack/javascripts/embed/address.js